import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import IconButton from '@/shared/components/IconButton/IconButton'
import { RichTextDoc, emptyRichText, richTextDocSchema } from '../../schema/richText'
import styles from './RichTextEditor.module.scss'

interface RichTextEditorProps {
  label: string
  value: RichTextDoc | undefined
  onChange: (doc: RichTextDoc) => void
}

/**
 * Formatted text, cut down to what the document schema allows.
 *
 * Every extension outside the whitelist is switched off rather than filtered
 * afterwards: with no heading, blockquote, code or horizontal rule in the
 * editor, there is no way to type a node the schema would then refuse. The
 * `safeParse` below is a second line, not the first one, and it also strips the
 * `target`, `rel` and `class` attributes Tiptap adds to links, which the
 * document has no business storing.
 */
function RichTextEditor(props: RichTextEditorProps) {
  const { label, value, onChange } = props
  const { t } = useTranslation()

  /*
    What this component last sent out. Without it, writing the value back into
    the editor on every render would fight the caret on every keystroke.
  */
  const lastEmitted = useRef<string>('')

  const editor = useEditor({
    // The brief asks for this explicitly: rendering on the first pass breaks
    // wherever the editor is mounted during a server render.
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        link: {
          openOnClick: false,
          autolink: true,
          // The same four the schema accepts. Anything else cannot be typed in.
          protocols: ['http', 'https', 'mailto', 'tel']
        }
      })
    ],
    content: value ?? emptyRichText,
    onUpdate: ({ editor: instance }) => {
      const parsed = richTextDocSchema.safeParse(instance.getJSON())
      if (!parsed.success) return
      lastEmitted.current = JSON.stringify(parsed.data)
      onChange(parsed.data)
    }
  })

  /*
    Reload the editor when the document changes underneath it: switching the
    language being edited, or an undo. Skipped when the incoming value is the one
    this editor just produced, which is the common case and the one that would
    otherwise move the caret to the end on every letter.
  */
  useEffect(() => {
    if (!editor) return
    const incoming = JSON.stringify(value ?? emptyRichText)
    if (incoming === lastEmitted.current) return
    lastEmitted.current = incoming
    editor.commands.setContent(value ?? emptyRichText, { emitUpdate: false })
  }, [editor, value])

  const setLink = useCallback(() => {
    if (!editor) return
    const previous = editor.getAttributes('link').href as string | undefined
    // eslint-disable-next-line no-alert
    const href = window.prompt(t('WEBSITE.RICH_TEXT.LINK_PROMPT'), previous ?? 'https://')
    if (href === null) return
    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }, [editor, t])

  if (!editor) return null

  return (
    <div className={styles.wrapper}>
      <p className={`label-bold-medium ${styles.label}`}>{label}</p>

      <div className={styles.toolbar}>
        <IconButton
          icon="las la-bold icon-medium"
          color={editor.isActive('bold') ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.RICH_TEXT.BOLD')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <IconButton
          icon="las la-italic icon-medium"
          color={editor.isActive('italic') ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.RICH_TEXT.ITALIC')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <IconButton
          icon="las la-list-ul icon-medium"
          color={editor.isActive('bulletList') ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.RICH_TEXT.BULLET_LIST')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <IconButton
          icon="las la-list-ol icon-medium"
          color={editor.isActive('orderedList') ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.RICH_TEXT.ORDERED_LIST')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <IconButton
          icon="las la-link icon-medium"
          color={editor.isActive('link') ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.RICH_TEXT.LINK')}
          onClick={setLink}
        />
        <IconButton
          icon="las la-unlink icon-medium"
          color="primary"
          disabled={!editor.isActive('link')}
          ariaLabel={t('WEBSITE.RICH_TEXT.UNLINK')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        />
      </div>

      <EditorContent editor={editor} className={styles.content} />
    </div>
  )
}

export default RichTextEditor
